import { memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import JsFileDownloader from 'js-file-downloader';

import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { Popover } from 'components/Popover';
import { PopoverOption } from 'components/Popover/components/PopoverItem';

import { Store } from 'types';
import { getContentType, useOnClickOutside } from 'utils';

import { ReactComponent as FatArrowDownIcon } from 'assets/images/fat-arrow-down.svg';
import { ReactComponent as PlayIcon } from 'assets/images/play.svg';

import styles from './styles.module.scss';

const downloadPopoverOptions: PopoverOption[] = [
  { label: 'Download .png' },
  { label: 'Download .svg' },
];

type Props = {
  onClose: () => void;
  onDelete: () => void;
  onEdit: () => void;
};

export const ArtworkModal: React.FC<Props> = memo(function ArtworkModal({
  onClose,
  onDelete,
  onEdit,
}) {
  const { previewMode, data } = useSelector((store: Store) => store.artwork);
  const [downloadPopoverOpen, setDownloadPopoverOpen] = useState(false);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  useOnClickOutside(
    popoverRef,
    useCallback(() => setDownloadPopoverOpen(false), []),
  );

  const hanldePlayClick = useCallback(() => {
    videoRef.current?.play();
    setShowPlayer(true);
  }, []);

  const handleDownloadClick = useCallback(() => {
    setDownloadPopoverOpen(true);
  }, []);

  const handleDownloadPopoverClick = useCallback(
    (selectedOption: PopoverOption) => {
      let fileUrl: string | undefined = undefined;

      if (selectedOption.label === downloadPopoverOptions[0].label) {
        fileUrl = data!.qrcodeImagePng;
      } else {
        fileUrl = data!.qrcodeImageSvg;
      }

      new JsFileDownloader({
        url: fileUrl,
        nameCallback(extractedName) {
          return `${data!.name} - ${extractedName}`;
        },
      });

      setDownloadPopoverOpen(false);
    },
    [data],
  );

  return (
    <Modal onClose={onClose} containerClassName={styles.container}>
      <h1>
        {previewMode ? (
          'Preview'
        ) : (
          <>
            {data?.name}
            {/* <span>No category</span> */}
          </>
        )}
      </h1>
      {!previewMode && (
        <div className={styles.actions_container}>
          <Button title="Edit" className={styles.edit_button} onClick={onEdit} />
          <Button title="Delete" className={styles.delete_button} onClick={onDelete} />
        </div>
      )}
      <div className={styles.artwork_container}>
        <p className={styles.label}>Artwork:</p>
        {getContentType(data!.media[0].contentType) === 'image' && (
          <img src={data!.media[0].url} alt="artwork" />
        )}
        {getContentType(data!.media[0].contentType) === 'video' && (
          <div className={styles.video_player}>
            <video controls={showPlayer} ref={videoRef}>
              <source src={`${data!.media[0].url}#t=0.1`} type="video/mp4" />
              <track default kind="captions" srcLang="en" />
            </video>
            {!showPlayer && (
              <div>
                <div onClick={hanldePlayClick}>
                  <PlayIcon />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.trigger_container}>
        {data!.pointerImage && (
          <div className={styles.artwork_triggerer}>
            <p className={styles.label}>Image to trigger artwork:</p>
            <img src={data!.pointerImage} alt="artwork triggerer" />
          </div>
        )}
        <div className={styles.qr_code}>
          <div>
            <p className={styles.label}>QR code:</p>
            <button
              type="button"
              className={styles.download_button}
              onClick={handleDownloadClick}
              ref={downloadButtonRef}>
              Download
              <FatArrowDownIcon />
            </button>
            {downloadPopoverOpen && (
              <Popover
                bottom
                right
                ref={popoverRef}
                anchorEl={downloadButtonRef}
                options={downloadPopoverOptions}
                onClick={handleDownloadPopoverClick}
              />
            )}
          </div>
          <div>
            <img src={data!.qrcodeImageSvg} alt="QR code" />
          </div>
        </div>
      </div>
      <div>
        <p className={styles.label}>Link:</p>
        <h6>{(data!.media[1] || data!.media[0]).url}</h6>
      </div>
    </Modal>
  );
});
